import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  expectedSetsToDate,
  formatTargetMetricValue,
  muscleKey,
  type MuscleState,
  type UnitsPreference,
  type WeekProgress,
} from "@heraion/shared";

import { CoverageRowShell } from "../primitives/CoverageRowShell";
import {
  LANDMARK_ZONE_GLYPH,
  NO_BAND_GLYPH,
  landmarkZoneWord,
  noBandWord,
} from "../primitives/LandmarkBar";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { IncompleteNote, incompleteWord } from "../primitives/IncompleteNote";
import type { TFn } from "../i18nTypes";
import type { UiTheme } from "../theme";
import { weightUnitLabels } from "../weightUnitLabels";
import type { CoverageRow } from "./muscleCoverage";
import type { ProgressViewer } from "./types";
import { MuscleGapProposal } from "./MuscleGapProposal";

// PG-64 fetta 2: one line per muscle, hard sets done against the band's pace
// by today (never kg, §16.3), read off the coverage model's own `landmark`.
// Under it, as on the row it replaced: the athlete's own target as a second
// reference, and «Close the gap» on a muscle under MEV.

/** A row's place against the week's pace. `open`: out of the band's pace,
 *  shown before «see all». */
export type CompactReading = {
  entry: CoverageRow;
  /** Sets the band asks for by today; null without a published band. */
  expected: number | null;
  /** Sets behind the pace, 0 when on it. */
  behind: number;
  open: boolean;
};

// Under half a set behind reads as «on pace»: the sets are fractional
// (a synergist earns a share), and «0.3 behind» is noise, not a gap.
const BEHIND_FLOOR = 0.5;

export const compactReading = (
  entry: CoverageRow,
  week: WeekProgress,
): CompactReading => {
  const { band, zone, weeklySets } = entry.landmark;
  const expected = band ? expectedSetsToDate(band, week) : null;
  const gap = expected === null ? 0 : expected - weeklySets;
  const behind = gap >= BEHIND_FLOOR ? gap : 0;
  return { entry, expected, behind, open: behind > 0 || zone === "above-mrv" };
};

// The pace in words, for the ear: the glyph below is hidden from it.
const stateA11y = (t: TFn, state: MuscleState, locale: string): string => {
  if (state.kind === "none") {
    return t("progress.muscleState.none", {
      defaultValue: "No hard sets logged yet",
    });
  }
  if (state.kind === "thin") {
    return t("progress.muscleState.thin", {
      n: state.sessions,
      needed: state.needed,
      defaultValue: "Too little history for a pace: {{n}} of {{needed}} sessions",
    });
  }
  const pct = new Intl.NumberFormat(locale).format(Math.abs(state.stepPct));
  if (state.stepPct > 0) {
    return t("progress.muscleState.a11yOver", {
      pct,
      defaultValue: "recent load {{pct}}% over the 28-day pace",
    });
  }
  if (state.stepPct < 0) {
    return t("progress.muscleState.a11yUnder", {
      pct,
      defaultValue: "recent load {{pct}}% under the 28-day pace",
    });
  }
  return t("progress.muscleState.a11yLevel", {
    defaultValue: "recent load level with the 28-day pace",
  });
};

// The pace as a glyph, its direction the glyph's own: no colour carries it.
const paceGlyph = (state: MuscleState): string | null =>
  state.kind !== "pace"
    ? null
    : state.stepPct > 0
      ? "↗"
      : state.stepPct < 0
        ? "↘"
        : "→";

export type MuscleCoverageCompactRowProps = {
  theme: UiTheme;
  reading: CompactReading;
  units: UnitsPreference;
  locale: string;
  /** Every row but the first carries the hairline above it. */
  divider: boolean;
  /** The coach reads the athlete's target without «your». */
  viewer?: ProgressViewer;
};

export function MuscleCoverageCompactRow({
  theme,
  reading: { entry, expected, behind },
  units,
  locale,
  divider,
  viewer = "athlete",
}: MuscleCoverageCompactRowProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { landmark, state, row, proposal } = entry;
  const labels = weightUnitLabels(t);
  // ⛔ The band and the gap are read in sets, whatever the row's metric
  // (§16.3); only the athlete's own target keeps its own unit.
  const sets = (v: number) =>
    formatTargetMetricValue(v, "hard-sets", units, locale, labels);
  const target =
    row.target == null
      ? null
      : t(
          viewer === "coach"
            ? "progress.volumeLandmark.athleteTarget"
            : "progress.volumeLandmark.yourTarget",
          {
            v: formatTargetMetricValue(row.target, row.metric, units, locale, labels),
            defaultValue: viewer === "coach" ? "target {{v}}" : "your target {{v}}",
          },
        );
  const name = t(muscleKey(landmark.muscle));
  const done =
    expected === null
      ? sets(landmark.weeklySets)
      : t("progress.coverageCompact.doneOfExpected", {
          done: sets(landmark.weeklySets),
          expected: sets(expected),
          defaultValue: "{{done}} / {{expected}} by today",
        });
  const gapText =
    landmark.band === undefined || landmark.zone === undefined
      ? `${NO_BAND_GLYPH} ${noBandWord(t)}`
      : behind > 0
        ? t("progress.coverageCompact.behind", {
            n: sets(behind),
            defaultValue: "{{n}} behind",
          })
        : landmark.zone === "above-mrv"
          ? `${LANDMARK_ZONE_GLYPH["above-mrv"]} ${landmarkZoneWord(t, "above-mrv")}`
          : t("progress.coverageCompact.onPace", { defaultValue: "on pace" });
  const glyph = paceGlyph(state);
  const paceSpoken = stateA11y(t, state, locale);
  // For the ear, the numbers carry their unit, and the zone the figure paints
  // is said in words (its colour's non-colour twin).
  const doneSpoken =
    expected === null
      ? t("progress.volumeLandmark.sets", {
          count: landmark.weeklySets,
          n: sets(landmark.weeklySets),
        })
      : t("progress.coverageCompact.doneOfExpectedA11y", {
          count: expected,
          done: sets(landmark.weeklySets),
          expected: sets(expected),
        });
  const zoneSpoken = landmark.zone ? landmarkZoneWord(t, landmark.zone) : null;
  const incomplete = row.incomplete === true;
  const spoken = [
    name,
    doneSpoken,
    zoneSpoken,
    gapText,
    target,
    paceSpoken,
    incomplete ? incompleteWord(t) : null,
  ].filter(
    (x, i, all): x is string => Boolean(x) && all.indexOf(x) === i,
  );
  return (
    <CoverageRowShell
      theme={theme}
      name={name}
      value={done}
      valueExtra={glyph ? <Text style={styles.pace} {...hiddenFromScreenReader()}>{glyph}</Text> : null}
      spoken={spoken.join(", ")}
      bar={
        landmark.band && landmark.zone
          ? { weeklySets: landmark.weeklySets, band: landmark.band, zone: landmark.zone, here: true }
          : undefined
      }
      divider={divider}
      after={
        <>
          {/* Outside the accessible node, so the pill stays pressable. */}
          {incomplete ? <IncompleteNote theme={theme} reasons={row.incompleteReasons} /> : null}
          {proposal ? (
            <MuscleGapProposal
              theme={theme}
              proposal={proposal}
              muscleLabel={name}
              formatSets={sets}
              locale={locale}
              viewer={viewer}
            />
          ) : null}
        </>
      }
    >
      <View style={styles.bottom} {...hiddenFromScreenReader()}>
        <Text style={styles.gap}>{gapText}</Text>
        {target ? <Text style={styles.target}>{target}</Text> : null}
      </View>
    </CoverageRowShell>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  const mono = { fontFamily: ty.fonts.mono, fontVariant: ["tabular-nums" as const] };
  return StyleSheet.create({
    bottom: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.s2,
    },
    // The athlete's own target, quiet: the second reference.
    target: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600", fontVariant: ["tabular-nums"] },
    // Neutral ink: a pace is not a verdict, the glyph says the direction.
    pace: { ...mono, color: p.ink2, fontSize: ty.sizes.md, fontWeight: "700" },
    gap: { ...mono, color: p.ink2, fontSize: ty.sizes.md },
  });
}
